import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { Glyphicon, Button, Col, Row, ControlLabel, FormControl, FormGroup, HelpBlock, InputGroup, Popover, OverlayTrigger } from 'react-bootstrap';
import { ModalDialog } from 'axway-react-ui';
import * as utils from 'components/utility/utils';

class JoinInfoDialogContent extends ModalDialog.Content {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.validationMsgs = {};

		this.changed = this.changed.bind(this);
		this.onChangeParentField = this.onValueChanged.bind(this, 'parentField');
		this.onChangeJoinModel = this.onChangeJoinModel.bind(this);
		this.onChangeJoinField = this.onValueChanged.bind(this, 'joinField');
		this.onChangeJoinType = this.onValueChanged.bind(this, 'joinType');
		this.onJoinValuesAs = this.onValueChanged.bind(this, 'joinAs');
		this.onJoinAsFieldName = this.onValueChanged.bind(this, 'joinAsFieldName');
		this.validateJoinAsFieldName = this.validateJoinAsFieldName.bind(this);
		this.validateJoinModel = this.validateJoinModel.bind(this);

		this.registerFieldValidation('parentField');
		this.registerFieldValidation('joinModel', this.validateJoinModel);
		this.registerFieldValidation('joinField');
		this.registerFieldValidation('joinType');
		this.registerFieldValidation('joinAs');
		this.registerFieldValidation('joinAsFieldName', this.validateJoinAsFieldName);

		this.compareCaseInsensitive = (a, b) => a.localeCompare(b);

		this.JOIN_LABELS = {
			inner_join: this.intl.formatMsg('Inner join (matching rows)'),
			left_join: this.intl.formatMsg('Left join (all rows)')
		};
	}

	componentWillMount() {
		// If there's no value selected for the lists then emit the first as a "change"
		// to ensure the parent state is correct.
		const initState = {
			parentField: this.props.parentField,
			joinModel: this.props.joinModel,
			joinField: this.props.joinField,
			joinType: this.props.joinType,
			joinAs: this.props.joinAs,
			joinAsFieldName: this.props.joinAsFieldName
		};

		if (!initState.parentField) {
			const fields = this.getFieldNamesFromModel(this.props.parentModel);
			initState.parentField = fields && fields[0];
		}
		initState.joinType = initState.joinType
			|| (this.props.availableJoinTypes && this.props.availableJoinTypes[0])
			|| 'inner_join';
		initState.joinAs = initState.joinAs || 'array';

		// Push the initial values into the state.
		this.setState({
			...initState
		});
	}

	componentDidMount() {
		// Trigger the update so the parent gets the initial values
		this.changed();
	}

	onChangeJoinModel(ev) {
		const value = ev.target.value;
		const state = { joinModel: value };
		const emptyName = !this.state.joinAsFieldName;
		const sameName = this.state.joinAsFieldName === this.state.joinModel;

		if (emptyName || sameName) {
			state.joinAsFieldName = value;
		}

		const fields = this.getFieldNamesFromModel(value);
		state.joinField = fields && fields[0];

		this.setFieldState(state, () => {
			this.changed();
		});
	}

	onValueChanged(field, ev) {
		this.setFieldState(field, ev.target.value, () => {
			this.changed();
		});
	}

	validateJoinModel() {
		if (!(this.state.joinModel && this.state.joinModel.length > 0)) {
			this.validationMsgs.joinModel = this.intl.formatMsg('Model is required.');
			return false;
		}
		return true;
	}

	validateJoinAsFieldName() {
		this.validationMsgs.joinAsFieldName = null;

		if (this.state.joinAs === 'fields') {
			return true;
		}

		const value = utils.trim(this.state.joinAsFieldName).toLowerCase();
		const { fields } = this.props;

		if (!value) {
			this.validationMsgs.joinAsFieldName = this.intl.formatMsg('Field name is required.');
			return false;
		} else if (value !== this.props.joinAsFieldName.toLowerCase()
				&& fields.filter(f => f.toLowerCase() === value).length !== 0) {
			this.validationMsgs.joinAsFieldName = this.intl.formatMsg('A field already exists with this name.');
			return false;
		}
		return true;
	}

	changed() {
		const { onChange } = this.props;
		if (!onChange) {
			return;
		}
		const join = this.toJoin();
		onChange(join);
	}

	toJoin() {
		return {
			parentField: this.state.parentField,
			joinType: this.state.joinType,
			joinModel: this.state.joinModel,
			joinField: this.state.joinField,
			joinAs: this.state.joinAs,
			joinAsFieldName: (this.state.joinAs !== 'fields')
				? this.state.joinAsFieldName : null
		};
	}

	getJoinModelOptions() {
		const { parentModel, models } = this.props;
		const options = [];
		if (!models) {
			return options;
		}
		options.push(
			<option key="null" value="">
				{this.intl.formatMsg('Select a model')}
			</option>
		);

		Object.keys(models)
			.filter(m => (
				models[m].name.toLowerCase() !== parentModel.toLowerCase()	// Not parent
				&& Object.keys(models[m].fields).length > 0 // Has fields
			))
			.sort(this.compareCaseInsensitive)
			.forEach((model, i) => {
				options.push(
					<option key={i} value={models[model].name}>
						{models[model].name}
					</option>
				);
			});
		return options;
	}

	getFieldNamesFromModel(modelName) {
		if (!modelName) {
			return [];
		}

		const { models } = this.props;
		if (models) {
			const selectedModel = models[modelName];
			if (selectedModel) {
				const fields = Object.keys(selectedModel.fields);
				if (!(selectedModel.metadata && selectedModel.metadata.primarykey === null)) {
					// Push the primary key as a join option
					fields.push((selectedModel.metadata && selectedModel.metadata.primarykey) || 'id');
				}
				return fields.sort(this.compareCaseInsensitive);
			} else {
				// TODO: show an actual error if this is a valid case
				// eslint-disable-next-line no-console
				console.error('failed to find selected model:', modelName);
			}
		}
		return null;
	}

	getFieldOptionsFromModel(modelName) {
		const fields = this.getFieldNamesFromModel(modelName);
		return this.getFieldOptions(fields);
	}

	getFieldOptions(fields) {
		const fieldOptions = fields
			.map((field, i) => {
				return (
					<option key={i} value={field}>
						{field}
					</option>
				);
			}
			);
		return fieldOptions;
	}

	/**
	 * Render the control for selecting the join type. If there is no choice then
	 * it's rendered as a static.
	 */
	renderJoinTypeFormControl() {
		const joinTypes = this.props.availableJoinTypes || [ 'inner_join', 'outer_join' ];

		if (joinTypes.length === 1) {
			// No choice, render it as a static label.
			return (
				<FormControl.Static className="join-type">
					{this.JOIN_LABELS[joinTypes[0]]}
				</FormControl.Static>
			);
		} else {
			const { joinType } = this.state;
			return (
				<FormControl
					componentClass="select"
					onChange={this.onChangeJoinType}
					value={joinType}
					className="join-type"
				>
					<option value="inner_join">
						{this.JOIN_LABELS.inner_join}
					</option>
					<option value="left_join">
						{this.JOIN_LABELS.left_join}
					</option>
				</FormControl>
			);
		}
	}

	renderErrors(fieldName, fieldNameProps) {
		if (fieldNameProps.validationState === 'error') {
			return (
				<HelpBlock>
					{ this.validationMsgs[fieldName] }
				</HelpBlock>
			);
		}

		return null;
	}

	render() {
		const { parentModel, validate } = this.props;
		const { parentField, joinModel, joinField, joinAs, joinAsFieldName } = this.state;

		const parentFieldOptions = this.getFieldOptionsFromModel(parentModel);
		const noModel = !joinModel;
		const joinModelOptions = this.getJoinModelOptions();
		const joinModelFields = this.getFieldOptionsFromModel(joinModel);

		const joinModelValidationProps = this.getFieldValidationProps('joinModel');
		const joinAsFieldNameValidationProps = this.getFieldValidationProps('joinAsFieldName');

		const parentModelPopover = (
			<Popover id="join-info-parent-popover" className="info-popover" arrowOffsetTop={20} title={this.intl.formatMsg('Parent Model')}>
				<div>
					{this.intl.formatMsg('The parent model represents the main model of the join. It\'s similar to a master table in a master-detail relationship.')}
				</div>
			</Popover>
		);

		const joinToPopover = (
			<Popover id="join-to-popover" className="info-popover" arrowOffsetTop={20} title={this.intl.formatMsg('Join to...')}>
				<div>
					{this.intl.formatMsg('This is the model that will be joined with other models to create a common data set.')}
				</div>
			</Popover>
		);

		const mergeAsPopover = (
			<Popover id="merge-as-popover" className="info-popover" arrowOffsetTop={20} title={this.intl.formatMsg('Merge into as...')}>
				<div>
					{this.intl.formatMsg('Control how the fields from the join model are merged into the composite model.')}
				</div>
				<div>
					<dl>
						<dt>{this.intl.formatMsg('Array')}</dt>
						<dd>{this.intl.formatMsg('One to many relationship between parent model and join model. Matching join model data is merged into a new field of type Array on the composite model.', 'popup-array-txt')}</dd>

						<dt>{this.intl.formatMsg('Fields')}</dt>
						<dd>{this.intl.formatMsg('One to one relationship between parent model and join model. Fields from the matching join model are merged directly into the composite model.', 'popup-fields-txt')}</dd>

						<dt>{this.intl.formatMsg('Object')}</dt>
						<dd>{this.intl.formatMsg('One to one relationship between parent model and join model. Matching join model data is merged into a new field of type Object on the composite model.', 'popup-object-txt')}</dd>
					</dl>
				</div>
			</Popover>
		);

		return (
			<div className="join-info-content">
				<fieldset>
					<legend>
						{this.intl.formatMsg('Parent Model')}
						<OverlayTrigger
							trigger="click"
							rootClose
							placement="right"
							overlay={parentModelPopover}
						>
							<span className="action">
								<Button bsStyle="link"><Glyphicon glyph=" gg icon-GG-info" /></Button>
							</span>
						</OverlayTrigger>
					</legend>

					<Row>
						<Col sm={12}>
							<Col sm={4}>
								<ControlLabel>{this.intl.formatMsg('Model')}</ControlLabel>
								<FormControl.Static className="parent-model">
									{parentModel}
								</FormControl.Static>
							</Col>
							<Col sm={4}>
								<ControlLabel>{this.intl.formatMsg('Field')}</ControlLabel>
								<FormControl
									componentClass="select"
									onChange={this.onChangeParentField}
									value={parentField}
									className="parent-fields"
								>
									{parentFieldOptions}
								</FormControl>
							</Col>
						</Col>
					</Row>
				</fieldset>

				<fieldset>
					<legend>
						{this.intl.formatMsg('Join to...')}
						<OverlayTrigger
							trigger="click"
							rootClose
							placement="right"
							overlay={joinToPopover}
						>
							<span className="action">
								<Button bsStyle="link"><Glyphicon glyph=" gg icon-GG-info" /></Button>
							</span>
						</OverlayTrigger>
					</legend>

					<Row>
						<Col sm={12}>
							<Col sm={4}>
								<FormGroup
									controlId="joinModelCtrl"
									className="join-model-group"
									{...joinModelValidationProps}
								>
									<ControlLabel>{this.intl.formatMsg('Model')}</ControlLabel>
									<FormControl
										componentClass="select"
										onChange={this.onChangeJoinModel}
										value={joinModel}
										className="join-model"
									>
										{joinModelOptions}
									</FormControl>
									{validate && this.renderErrors('joinModel', joinModelValidationProps)}
								</FormGroup>
							</Col>
							<Col sm={4}>
								<ControlLabel>{this.intl.formatMsg('Field')}</ControlLabel>
								<FormControl
									componentClass="select"
									onChange={this.onChangeJoinField}
									disabled={noModel}
									value={joinField}
									className="join-fields"
								>
									{joinModelFields}
								</FormControl>
							</Col>
							<Col sm={4}>
								<ControlLabel>{this.intl.formatMsg('Join type')}</ControlLabel>
								{this.renderJoinTypeFormControl()}
							</Col>
						</Col>
					</Row>
				</fieldset>

				<fieldset>
					<legend>
						{this.intl.formatMsg('Merge into as...')}
						<OverlayTrigger
							trigger="click"
							rootClose
							placement="right"
							overlay={mergeAsPopover}
						>
							<span className="action">
								<Button bsStyle="link"><Glyphicon glyph=" gg icon-GG-info" /></Button>
							</span>
						</OverlayTrigger>
					</legend>

					<Row>
						<Col sm={12}>
							<Col sm={4}>
								<ControlLabel>{this.intl.formatMsg('Join model value as')}</ControlLabel>
								<FormControl
									componentClass="select"
									onChange={this.onJoinValuesAs}
									value={joinAs}
									disabled={noModel}
									className="join-as"
								>
									<option value="array">
										{this.intl.formatMsg('Array')}
									</option>
									<option value="fields">
										{this.intl.formatMsg('Fields')}
									</option>
									<option value="object">
										{this.intl.formatMsg('Object')}
									</option>
								</FormControl>
							</Col>
							{this.state.joinAs === 'fields' ? (
								<Col sm={8} className="message">
									<HelpBlock>
										{this.intl.formatHTMLMsg('All fields from {joinModel} will merge into the new model.  You can then selectively choose which fields to exclude.', {
											joinModel: <b>{joinModel}</b>
										})}
									</HelpBlock>
								</Col>
							) : (
								<Col sm={4}>
									<FormGroup
										controlId="joinAsFieldNameCtrl"
										className="join-as-field-name-group"
										{...joinAsFieldNameValidationProps}
									>
										<ControlLabel>{this.intl.formatMsg('Field name')}</ControlLabel>
										<InputGroup>
											<FormControl
												type="text"
												onChange={this.onJoinAsFieldName}
												value={joinAsFieldName}
												disabled={noModel}
												className="join-as-field-name"
											/>
											<InputGroup.Addon>*</InputGroup.Addon>
										</InputGroup>
										{validate && this.renderErrors('joinAsFieldName', joinAsFieldNameValidationProps)}
									</FormGroup>
								</Col>
							)}
						</Col>
					</Row>
				</fieldset>
			</div>
		);
	}
}

JoinInfoDialogContent.propTypes = {
	/**
	 * The parent model on which the join will be performed
	 */
	parentModel: PropTypes.string.isRequired,

	/**
	 * The parent model's field on which the join will be performed
	 */
	parentField: PropTypes.string,

	/**
	 * Models available for selection
	 */
	models: PropTypes.object.isRequired,

	/**
	 * A list of field names in the composite model.
	 */
	fields: PropTypes.arrayOf(PropTypes.string),

	/**
	 * The pre-selected model name
	 */
	joinModel: PropTypes.string,

	/**
	 * The pre-selected join type (left_join/inner_join)
	 */
	joinType: PropTypes.string,

	/**
	 * The possible joins available on this model
	 */
	availableJoinTypes: PropTypes.array,

	/**
	 * The pre-selected model field
	 */
	joinField: PropTypes.string,

	/**
	 * The pre-selected merge type (object/array/fields)
	 */
	joinAs: PropTypes.string,

	/**
	 * The pre-specified final merged field name
	 */
	joinAsFieldName: PropTypes.string,

	/**
	 * Called on any change in the form
	 * Returns: type, model, field, joinAs, joinAsFieldName
	 */
	onChange: PropTypes.func
};

JoinInfoDialogContent.defaultProps = {
	joinModel: '',
	joinType: 'left_join',
	joinField: '',
	joinAs: 'array',
	joinAsFieldName: ''
};

export default injectIntl(JoinInfoDialogContent);
